import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';

import indicatorsData from '../data/indicators-data.json';
import filterLabels from '../data/filter-labels.json';

import Layout from '../components/layout';
import SEO from '../components/seo';

function Indicators() {
  const [ data, setData ] = useState({});
  const [ filter, setFilter ] = useState({});
  const [ filterValue, setFilterValue ] = useState('');
  const [ disabled, setDisabled ] = useState(false);
  const [ chartData, setChartData ] = useState({});

  const eurostatUrl = '//ec.europa.eu/eurostat/wdds/rest/data/v2.1/json/en/';

  const updateIndicatorData = event => {
    setData({});

    setFilter(Object.keys(indicatorsData[event.target.value].filters).reduce((acc, cur) => ({
      ...acc,
      [cur]: {
        option: indicatorsData[event.target.value].filters[cur],
        selected: [],
      },
    }), {}));

    setFilterValue(`${event.target.value}?sinceTimePeriod=${indicatorsData[event.target.value].timePeriod}&precision=1`);
  };

  const updateOption = (event, type) => {
    const newFilter = { ...filter };

    if (event.target.value) {
      const idx = filter[type].selected.indexOf(event.target.value);
      if (idx === -1) {
        newFilter[type].selected.push(event.target.value);
      }
      else if (idx !== -1) {
        newFilter[type].selected.splice(idx, 1);
      }
    }
    else {
      newFilter[type].selected = [];
    }

    setFilter(newFilter);
  };

  const updateRegion = event => {
    const idx = filter.geo.selected.indexOf(event.target.value);

    const newFilter = { ...filter };

    if (event.target.checked && idx === -1) {
      newFilter.geo.selected.push(event.target.value);
    }
    else if (idx !== -1) {
      newFilter.geo.selected.splice(idx, 1);
    }

    setFilter(newFilter);
  };

  useEffect(() => {
    const disable = Object.values(filter).some(value => value.option && !value.selected.length);
    setDisabled(disable);
  }, [ filterValue, filter, disabled ]);

  const applyFilters = () => {
    const queryData = [ filterValue ].concat(
      Object.keys(filter).map(key => filter[key].selected.map(option => `${key}=${option}`).join('&')),
    );

    const query = queryData.filter(value => value.length > 0).join('&');

    axios.get(eurostatUrl + query).then(response => {
      const getValue = () => {
        if (response.data.status) {
          const operatingValues = response.data.value;
          const missingValues = response.data.status;

          Object.keys(missingValues).forEach(status => {
            Object.assign(operatingValues, { [status]: null });
          });
        }
        return (Object.values(response.data.value));
      };

      setData({
        title: response.data.label,
        region: Object.values(response.data.dimension.geo.category.label),
        time: Object.values(response.data.dimension.time.category.label),
        value: getValue(),
        source: response.data.source,
        updated: response.data.updated,
      });

      const years = Object.values(response.data.dimension.time.category.index);

      const chunk = (input, size) => input.reduce(
        (arr, item, idx) => (
          idx % size === 0
            ? [ ...arr, [ item ] ]
            : [ ...arr.slice(0, -1), [ ...arr.slice(-1)[0], item ] ]
        ),
        [],
      );

      const finalValues = chunk(getValue(), years.length);
      const regionLabel = Object.values(response.data.dimension.geo.category.label);
      const colors = [
        '#49B2AA',
        '#05988D',
        '#005D56',
        '#FF8268',
        '#F73208',
        '#971A00',
        '#FFC233',
        '#F79608',
        '#975A00',
        '#70A3CE',
        '#2A6DA5',
        '#084273',
        '#8A79D4',
        '#4B35B0',
        '#230F7B',
      ];

      setChartData({
        labels: Object.values(response.data.dimension.time.category.label),
        datasets: finalValues.map((values, idx) => ({
          label: regionLabel[idx],
          data: values,
          borderWidth: 4,
          backgroundColor: 'transparent',
          lineTension: 0,
          borderColor: colors[idx],
        })),
      });
    });
  };

  return (
    <Layout>
      <SEO title="Indicators" />
      <div className="indicators-wrapper">
        <div className="indicator-filters">
          <label htmlFor="indicator-options">Indicators</label>
          <div className="indicator-options__wrapper select__wrapper">
            <select
              name="indicator"
              id="indicator-options"
              className="indicators__options"
              onChange={ updateIndicatorData }
            >
              <option value="" hidden>--Please choose an option--</option>
              { (Object.keys(indicatorsData) || []).map(name => (
                <option value={ name } key={ `filter-${name}` }>
                  { indicatorsData[name].label }
                </option>
              )) }
            </select>
          </div>

          { Object.keys(filter).filter(item => item !== 'geo').map(item => (
            <div key={ `${item}-wrapper` }>
              <label htmlFor={ `${item}-options` }>{ filterLabels[item].label }</label>
              <div className="indicator-options__wrapper select__wrapper">
                <select
                  name={ item }
                  id={ `${item}-options` }
                  className="indicator-filter"
                  onChange={ event => updateOption(event, item) }
                  value={ filter[item].selected[0] || '' }
                >
                  <option value="">--Please choose an option--</option>
                  { (filter[item].option || []).map((name, idx) => (
                    <option value={ name } key={ `${item}-${name}-${idx * idx}` }>
                      { filterLabels[item].options[name] }
                    </option>
                  )) }
                </select>

              </div>
            </div>
          )) }

          { !!filter.geo && !!filter.geo.option && (
            <>
              <button
                type="button"
                className="region-options__title"
                data-list="region-list"
              >
                Region
              </button>
              <ul className="indicators__region-options" id="region-list">
                { filter.geo.option.map((region, idx) => (
                  <li key={ `wrapper-${region}-${idx * idx}` }>
                    <label key={ `label-${region}-${idx * idx}` } className="indicators__options__label">
                      <input
                        type="checkbox"
                        onChange={ event => updateRegion(event, 'geo') }
                        name={ region }
                        value={ region }
                        className="indicators__options__checkbox"
                        key={ `checkbox-${region}-${idx * idx}` }
                        checked={ filter.geo.selected.indexOf(region) !== -1 }
                      />
                      <span className="indicators__options__checkbox__value">{ filterLabels.geo.options[region] }</span>
                      <span className="indicators__options__checkbox__checkmark" />
                    </label>
                  </li>
                )) }
              </ul>
            </>
          ) }

          <button
            type="button"
            disabled={ disabled }
            className="primary-apply"
            onClick={ applyFilters }
          >
            Apply
          </button>
        </div>
        <div className="chart-wrapper">
          <div className="chart-display">
            <Line
              data={ chartData }
              className="chart"
              options={
              {
                responsive: true,
                title: { text: `${data.title}`, display: true },
                legend: {
                  position: 'bottom',
                  label: {
                    boxWidth: 1,
                    fontSize: 10,
                  },
                },
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        maxTicksLimit: 20,
                      },
                      gridLines: {
                        display: true,
                        lineWidth: 1,
                      },
                    },
                  ],
                  xAxes: [
                    {
                      gridLines: {
                        display: true,
                        lineWidth: 0,
                      },
                    },
                  ],
                },
              }
            }
            />
          </div>
          <div className="indicator-footer">
            <span>
              Source of data:
              { ' ' }
              { data.source }
            </span>
            <p>
              Last updated on
              { ' ' }
              { data.updated }
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Indicators;
